// src/components/sla/ai/SlaAiDecisionFeed.js
// ─────────────────────────────────────────────────────────────────────────────
// Live log of SLA AI decisions (warnings, escalations, reassignments, recommendations)
// with type / status filters, "load more", and a click-through to the explainer.
// Refreshes on 'sla_ai_decision' / work order websocket events.
// ─────────────────────────────────────────────────────────────────────────────
import React, { memo, useCallback, useState } from 'react';
import {
    Alert, AlertDescription, AlertIcon, Badge, Box, Button, Flex, HStack, Icon, IconButton, Select, Skeleton, Stack, Text,
    Tooltip, VStack, useColorModeValue, useDisclosure,
} from '@chakra-ui/react';
import { MdArrowForward, MdAutoAwesome, MdRefresh } from 'react-icons/md';
import { useSlaAiDecisions } from '../../../hooks/useSlaAi';
import SlaDecisionExplainer from './SlaDecisionExplainer';
import SlaGradeBadge from './SlaGradeBadge';
import { ACTION_META, STATUS_META, fmtMinutes, fmtRelative } from './slaAiFormat';

export const SlaDecisionCard = memo(function SlaDecisionCard({ d, onOpen }) {
    const meta = ACTION_META[d.decision_type] || ACTION_META.escalate;
    const st = STATUS_META[d.status] || STATUS_META.executed;
    const bg = useColorModeValue('white', 'gray.800');
    const hover = useColorModeValue('purple.50', 'whiteAlpha.100');
    const border = useColorModeValue('gray.200', 'gray.700');
    const muted = useColorModeValue('gray.500', 'gray.400');
    return (
        <Box
            as="button" type="button" textAlign="left" w="100%" bg={bg} borderWidth="1px" borderColor={border} borderLeftWidth="4px"
            borderLeftColor={`${meta.scheme}.400`} borderRadius="lg" p={3} _hover={{ bg: hover }} transition="background 0.15s"
            onClick={() => onOpen(d)} aria-label={`${meta.label} for work order ${d.work_order_id}`}
        >
            <Flex gap={3} align="flex-start">
                <Icon as={meta.icon} color={`${meta.scheme}.500`} boxSize={5} mt={0.5} />
                <Box flex="1" minW={0}>
                    <HStack spacing={2} flexWrap="wrap" mb={0.5}>
                        <Text fontWeight="700" fontSize="sm">{meta.short}</Text>
                        <Text fontSize="sm" color={muted} noOfLines={1}>WO #{d.work_order_id}{d.work_order_title ? ` · ${d.work_order_title}` : ''}</Text>
                    </HStack>
                    <Text fontSize="sm" noOfLines={2}>{d.summary}</Text>
                    <HStack spacing={2} mt={1.5} flexWrap="wrap">
                        <Badge colorScheme={st.scheme} variant="subtle">{st.label}</Badge>
                        {d.escalation_level ? <Badge colorScheme="red" variant="outline">L{d.escalation_level}</Badge> : null}
                        {d.minutes_late ? <Badge colorScheme="red" variant="subtle">{fmtMinutes(d.minutes_late)} late</Badge> : null}
                        {d.new_assignee_name && <Badge colorScheme="purple" variant="subtle">→ {d.new_assignee_name}</Badge>}
                        <Text fontSize="xs" color={muted}>{fmtRelative(d.created_at)}</Text>
                    </HStack>
                </Box>
                {d.grade_letter && <SlaGradeBadge grade={d.grade_letter} score={d.grade_score} size="sm" projected />}
                <Icon as={MdArrowForward} color={muted} mt={1} />
            </Flex>
        </Box>
    );
});

function SlaAiDecisionFeed({ workOrderId, compact = false, title = 'AI decision log', pageSize = 20, defaultType = 'all', defaultStatus = 'all' }) {
    const [type, setType] = useState(defaultType);
    const [status, setStatus] = useState(defaultStatus);
    const feed = useSlaAiDecisions({ type, status, work_order_id: workOrderId }, { pageSize });
    const [selected, setSelected] = useState(null);
    const drawer = useDisclosure();
    const muted = useColorModeValue('gray.500', 'gray.400');
    const { refresh } = feed;

    const open = useCallback((d) => { setSelected(d); drawer.onOpen(); }, [drawer]);

    return (
        <Box>
            <Flex justify="space-between" align="center" mb={3} gap={2} flexWrap="wrap">
                <HStack><Icon as={MdAutoAwesome} color="purple.500" /><Text fontWeight="800">{title}</Text>{feed.total ? <Badge>{feed.total}</Badge> : null}</HStack>
                <HStack spacing={2}>
                    {!compact && (
                        <>
                            <Select size="sm" w="auto" value={type} onChange={(e) => setType(e.target.value)} aria-label="Decision type" borderRadius="md">
                                <option value="all">All actions</option>
                                {Object.entries(ACTION_META).map(([k, m]) => <option key={k} value={k}>{m.short}</option>)}
                            </Select>
                            <Select size="sm" w="auto" value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Decision status" borderRadius="md">
                                <option value="all">Any status</option>
                                {Object.entries(STATUS_META).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
                            </Select>
                        </>
                    )}
                    <Tooltip label="Refresh" hasArrow><IconButton size="sm" variant="ghost" icon={<MdRefresh />} aria-label="Refresh decisions" onClick={refresh} isLoading={feed.refreshing} /></Tooltip>
                </HStack>
            </Flex>

            {feed.error && (
                <Alert status="error" borderRadius="lg" mb={3}>
                    <AlertIcon /><AlertDescription flex="1" fontSize="sm">{feed.error}</AlertDescription>
                    <Button size="xs" onClick={() => feed.reload()}>Retry</Button>
                </Alert>
            )}
            {feed.loading && <Stack spacing={2}>{[0, 1, 2].map(i => <Skeleton key={i} h="72px" borderRadius="lg" />)}</Stack>}
            {!feed.loading && !feed.error && feed.rows.length === 0 && (
                <Box textAlign="center" py={8} color={muted}>
                    <Icon as={MdAutoAwesome} boxSize={8} mb={2} />
                    <Text fontWeight="600">No AI decisions{type !== 'all' || status !== 'all' ? ' match these filters' : ' yet'}.</Text>
                    <Text fontSize="sm">The SLA AI checks every open job every few minutes.</Text>
                </Box>
            )}
            <VStack align="stretch" spacing={2}>
                {feed.rows.map(d => <SlaDecisionCard key={d.id} d={d} onOpen={open} />)}
            </VStack>
            {feed.hasMore && <Button mt={3} size="sm" variant="ghost" w="100%" onClick={feed.loadMore} isLoading={feed.refreshing}>Load more</Button>}

            <SlaDecisionExplainer isOpen={drawer.isOpen} onClose={drawer.onClose} decision={selected} decisionId={selected?.id} onChanged={refresh} />
        </Box>
    );
}

export default memo(SlaAiDecisionFeed);
